import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  IconButton,
  Popover,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Quickreply as QuickreplyIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Image as ImageIcon,
  Videocam as VideocamIcon,
  Mic as MicIcon,
  AttachFile as AttachFileIcon,
  DragIndicator as DragIndicatorIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import type { InboxQuickMessageContentType, InboxQuickMessageTemplate } from '../types/inboxQuickMessage';
import { sortInboxQuickMessages } from '../utils/inboxQuickMessageSort';

interface QuickMessage extends InboxQuickMessageTemplate {}

interface QuickMessagesButtonProps {
  onSelectMessage: (message: string) => void;
  /** Envia modelo com mídia (WhatsApp / recovery). Se omitido, só texto é usado. */
  onSelectTemplate?: (template: InboxQuickMessageTemplate) => void | Promise<void>;
  channel?: 'whatsapp' | 'telegram' | 'instagram' | 'whatsapp_recovery' | 'team_chat';
  defaultMessages?: QuickMessage[];
  overlayZIndex?: number;
  size?: 'small' | 'medium';
  tooltip?: string;
  disabled?: boolean;
  /** Incrementar para abrir o popover (ex.: menu + do composer mobile). */
  openRequestId?: number;
  /** Mantém o botão invisível; use com openRequestId. */
  hideTrigger?: boolean;
}

const surfacePaperSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  borderRadius: 2,
} as const;

const supportsWhatsAppMedia = (channel: string) =>
  channel === 'whatsapp' || channel === 'whatsapp_recovery';

const contentTypeLabel: Record<InboxQuickMessageContentType, string> = {
  text: 'Texto',
  image: 'Imagem',
  video: 'Vídeo',
  audio: 'Áudio',
};

type SortableQuickMessageRowProps = {
  message: QuickMessage;
  onSelect: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onToggleFavorite: (event: React.MouseEvent<HTMLElement>) => void;
  secondaryPreview: (message: QuickMessage) => string;
};

function SortableQuickMessageRow({
  message,
  onSelect,
  onMenuOpen,
  onToggleFavorite,
  secondaryPreview,
}: SortableQuickMessageRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: message.id,
  });
  const ct = message.content_type || 'text';
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disablePadding
      secondaryAction={
        <IconButton edge="end" size="small" onClick={onMenuOpen} sx={{ color: 'text.secondary' }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      }
      sx={{ alignItems: 'stretch' }}
    >
      <IconButton
        size="small"
        aria-label="Reordenar"
        sx={{ cursor: 'grab', color: 'text.disabled', alignSelf: 'center', touchAction: 'none' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label={message.is_favorite ? 'Remover dos favoritos' : 'Favoritar'}
        onClick={onToggleFavorite}
        sx={{ alignSelf: 'center', color: message.is_favorite ? 'warning.main' : 'text.disabled' }}
      >
        {message.is_favorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
      </IconButton>
      <ListItemButton onClick={onSelect} sx={{ flex: 1, minWidth: 0, py: 0.75 }}>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: message.is_favorite ? 700 : 500 }}>
                {message.name}
              </Typography>
              {ct !== 'text' && <Chip size="small" label={contentTypeLabel[ct]} variant="outlined" />}
            </Box>
          }
          secondary={
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {secondaryPreview(message)}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  );
}

const QuickMessagesButton: React.FC<QuickMessagesButtonProps> = ({
  onSelectMessage,
  onSelectTemplate,
  channel = 'whatsapp',
  defaultMessages,
  overlayZIndex,
  size = 'medium',
  tooltip = 'Mensagens Rápidas',
  disabled = false,
  openRequestId,
  hideTrigger = false,
}) => {
  const { user } = useAuth();
  const currentUnitId = user?.current_unit?.id || '';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [messages, setMessages] = useState<QuickMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMessage, setEditingMessage] = useState<QuickMessage | null>(null);
  const [formName, setFormName] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formContentType, setFormContentType] = useState<InboxQuickMessageContentType>('text');
  const [formMediaUrl, setFormMediaUrl] = useState<string | null>(null);
  const [formMediaMime, setFormMediaMime] = useState<string | null>(null);
  const [formMediaFileName, setFormMediaFileName] = useState<string | null>(null);
  const [formVoiceNote, setFormVoiceNote] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const usesDatabase = channel !== 'team_chat';
  const storageKey = `quick_messages_${channel}`;
  const canAttachMedia = usesDatabase && supportsWhatsAppMedia(channel);

  const mapApiRows = (rows: any[]): QuickMessage[] =>
    sortInboxQuickMessages(
      (rows || []).map((row) => ({
        id: String(row.id),
        name: String(row.name || ''),
        message: String(row.message || ''),
        content_type: (row.content_type as InboxQuickMessageContentType) || 'text',
        media_url: row.media_url || null,
        media_mime_type: row.media_mime_type || null,
        media_file_name: row.media_file_name || null,
        is_voice_note: row.is_voice_note !== false,
        sort_order: Number(row.sort_order ?? 0),
        is_favorite: Boolean(row.is_favorite),
      })),
    );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const sortedMessages = useMemo(() => sortInboxQuickMessages(messages), [messages]);
  const sortableIds = useMemo(() => sortedMessages.map((m) => m.id), [sortedMessages]);

  const loadMessages = useCallback(async () => {
    if (!usesDatabase) return;
    if (!currentUnitId) {
      setMessages([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(getApiUrl('/inbox/quick-messages'), {
        params: { channel },
      });
      setMessages(mapApiRows(res.data?.data || []));
    } catch (error) {
      console.error('Erro ao carregar mensagens rápidas do banco:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [channel, currentUnitId, usesDatabase]);

  useEffect(() => {
    if (usesDatabase) {
      loadMessages();
      return;
    }

    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        setMessages(
          sortInboxQuickMessages(
            (JSON.parse(stored) as QuickMessage[]).map((m, index) => ({
              ...m,
              sort_order: m.sort_order ?? index,
              is_favorite: Boolean(m.is_favorite),
            })),
          ),
        );
      } catch (error) {
        console.error('Erro ao carregar mensagens rápidas:', error);
      }
      return;
    }

    if (defaultMessages?.length) {
      setMessages(defaultMessages);
      localStorage.setItem(storageKey, JSON.stringify(defaultMessages));
    }
  }, [storageKey, defaultMessages, usesDatabase, loadMessages]);

  const saveLocalMessages = (newMessages: QuickMessage[]) => {
    const normalized = sortInboxQuickMessages(
      newMessages.map((m, index) => ({
        ...m,
        sort_order: m.sort_order ?? index,
        is_favorite: Boolean(m.is_favorite),
      })),
    );
    setMessages(normalized);
    localStorage.setItem(storageKey, JSON.stringify(normalized));
  };

  const resetMediaForm = () => {
    setFormMediaUrl(null);
    setFormMediaMime(null);
    setFormMediaFileName(null);
    setFormVoiceNote(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (usesDatabase) loadMessages();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!openRequestId || !triggerRef.current) return;
    setAnchorEl(triggerRef.current);
    if (usesDatabase) void loadMessages();
  }, [openRequestId, usesDatabase, loadMessages]);

  const handleSelectTemplate = (template: QuickMessage) => {
    const ct = template.content_type || 'text';
    if (ct !== 'text' && template.media_url && onSelectTemplate && supportsWhatsAppMedia(channel)) {
      void onSelectTemplate(template);
      handleClose();
      return;
    }
    onSelectMessage(template.message);
    handleClose();
  };

  const handleOpenDialog = (message?: QuickMessage) => {
    if (message) {
      setEditingMessage(message);
      setFormName(message.name);
      setFormMessage(message.message);
      setFormContentType(message.content_type || 'text');
      setFormMediaUrl(message.media_url || null);
      setFormMediaMime(message.media_mime_type || null);
      setFormMediaFileName(message.media_file_name || null);
      setFormVoiceNote(message.is_voice_note !== false);
    } else {
      setEditingMessage(null);
      setFormName('');
      setFormMessage('');
      setFormContentType('text');
      resetMediaForm();
    }
    setOpenDialog(true);
    setMenuAnchorEl(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMessage(null);
    setFormName('');
    setFormMessage('');
    setFormContentType('text');
    resetMediaForm();
  };

  const handleMediaFile = async (file: File) => {
    if (!canAttachMedia || !currentUnitId) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(getApiUrl('/inbox/quick-messages/upload-media'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data;
      if (!data?.media_url) throw new Error('Upload sem URL');
      setFormContentType((data.content_type as InboxQuickMessageContentType) || 'image');
      setFormMediaUrl(data.media_url);
      setFormMediaMime(data.media_mime_type || file.type);
      setFormMediaFileName(data.media_file_name || file.name);
      setFormVoiceNote(data.is_voice_note !== false);
    } catch (error) {
      console.error('Erro ao enviar mídia:', error);
    } finally {
      setUploading(false);
    }
  };

  const formValid = () => {
    if (!formName.trim()) return false;
    if (formContentType === 'text') return Boolean(formMessage.trim());
    return Boolean(formMediaUrl);
  };

  const handleSaveMessage = async () => {
    if (!formValid()) return;

    if (usesDatabase) {
      if (!currentUnitId) return;
      try {
        setSaving(true);
        const payload = {
          name: formName.trim(),
          message: formMessage.trim(),
          channel,
          content_type: formContentType,
          media_url: formContentType === 'text' ? null : formMediaUrl,
          media_mime_type: formContentType === 'text' ? null : formMediaMime,
          media_file_name: formContentType === 'text' ? null : formMediaFileName,
          is_voice_note: formContentType === 'audio' ? formVoiceNote : false,
        };
        if (editingMessage) {
          await axios.put(getApiUrl(`/inbox/quick-messages/${editingMessage.id}`), payload);
        } else {
          await axios.post(getApiUrl('/inbox/quick-messages'), payload);
        }
        await loadMessages();
        handleCloseDialog();
      } catch (error) {
        console.error('Erro ao salvar mensagem rápida:', error);
      } finally {
        setSaving(false);
      }
      return;
    }

    const newMessages = [...messages];
    if (editingMessage) {
      const index = newMessages.findIndex((m) => m.id === editingMessage.id);
      if (index !== -1) {
        newMessages[index] = {
          id: editingMessage.id,
          name: formName.trim(),
          message: formMessage.trim(),
        };
      }
    } else {
      newMessages.push({
        id: Date.now().toString(),
        name: formName.trim(),
        message: formMessage.trim(),
        sort_order: newMessages.length,
        is_favorite: false,
      });
    }
    saveLocalMessages(newMessages);
    handleCloseDialog();
  };

  const handleDeleteMessage = async (id: string) => {
    if (usesDatabase) {
      try {
        await axios.delete(getApiUrl(`/inbox/quick-messages/${id}`));
        await loadMessages();
      } catch (error) {
        console.error('Erro ao excluir mensagem rápida:', error);
      }
      setMenuAnchorEl(null);
      setSelectedMessageId(null);
      return;
    }

    const newMessages = messages.filter((m) => m.id !== id);
    saveLocalMessages(newMessages);
    setMenuAnchorEl(null);
    setSelectedMessageId(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, messageId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedMessageId(messageId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMessageId(null);
  };

  const persistReorder = async (ordered: QuickMessage[]) => {
    const withOrder = ordered.map((m, index) => ({ ...m, sort_order: index }));
    setMessages(withOrder);
    if (usesDatabase) {
      try {
        await axios.put(getApiUrl('/inbox/quick-messages/reorder'), {
          channel,
          ordered_ids: withOrder.map((m) => m.id),
        });
      } catch (error) {
        console.error('Erro ao reordenar mensagens rápidas:', error);
        await loadMessages();
      }
      return;
    }
    saveLocalMessages(withOrder);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortedMessages.findIndex((m) => m.id === active.id);
    const newIndex = sortedMessages.findIndex((m) => m.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    void persistReorder(arrayMove(sortedMessages, oldIndex, newIndex));
  };

  const toggleFavoriteFor = async (message: QuickMessage) => {
    const nextFavorite = !message.is_favorite;
    if (usesDatabase) {
      try {
        await axios.put(getApiUrl(`/inbox/quick-messages/${message.id}`), {
          is_favorite: nextFavorite,
        });
        setMessages((prev) =>
          sortInboxQuickMessages(
            prev.map((m) => (m.id === message.id ? { ...m, is_favorite: nextFavorite } : m)),
          ),
        );
      } catch (error) {
        console.error('Erro ao favoritar mensagem rápida:', error);
      }
      return;
    }
    saveLocalMessages(
      messages.map((m) => (m.id === message.id ? { ...m, is_favorite: nextFavorite } : m)),
    );
  };

  const handleToggleFavoriteClick = (message: QuickMessage, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    void toggleFavoriteFor(message);
  };

  const secondaryPreview = (message: QuickMessage) => {
    const ct = message.content_type || 'text';
    if (ct !== 'text') {
      const label = contentTypeLabel[ct];
      const file = message.media_file_name ? ` · ${message.media_file_name}` : '';
      const cap = message.message ? ` — ${message.message}` : '';
      return `${label}${file}${cap}`;
    }
    return message.message;
  };

  const open = Boolean(anchorEl);
  const id = open ? 'quick-messages-popover' : undefined;
  const overlaySx = overlayZIndex ? { zIndex: overlayZIndex } : undefined;

  return (
    <>
      <Tooltip title={hideTrigger ? '' : tooltip}>
        <IconButton
          ref={triggerRef}
          onClick={handleClick}
          size={size}
          disabled={disabled}
          aria-hidden={hideTrigger}
          tabIndex={hideTrigger ? -1 : undefined}
          sx={{
            color: 'primary.main',
            flexShrink: 0,
            p: size === 'small' ? 0.75 : 1,
            '&:hover': {
              color: 'primary.dark',
              backgroundColor: 'action.hover',
            },
            transition: 'all 0.2s ease-in-out',
            ...(hideTrigger
              ? {
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                  opacity: 0,
                  pointerEvents: 'none',
                }
              : {}),
          }}
        >
          <QuickreplyIcon sx={{ fontSize: size === 'small' ? 20 : 24 }} />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={overlaySx}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: {
            ...surfacePaperSx,
            width: 320,
            maxHeight: 400,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {channel === 'team_chat' ? 'Ações rápidas' : 'Mensagens Rápidas'}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              color="primary"
              disabled={usesDatabase && !currentUnitId}
            >
              Nova
            </Button>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {sortedMessages.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Arraste para reordenar · estrela para favoritar
            </Typography>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !currentUnitId && usesDatabase ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Selecione uma unidade para ver ou cadastrar mensagens rápidas
              </Typography>
            </Box>
          ) : sortedMessages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Nenhuma mensagem rápida criada nesta unidade
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                color="primary"
                sx={{ mt: 2 }}
              >
                Criar primeira mensagem
              </Button>
            </Box>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                <List sx={{ p: 0, maxHeight: 320, overflow: 'auto' }}>
                  {sortedMessages.map((message) => (
                    <SortableQuickMessageRow
                      key={message.id}
                      message={message}
                      onSelect={() => handleSelectTemplate(message)}
                      onMenuOpen={(e) => handleMenuOpen(e, message.id)}
                      onToggleFavorite={(e) => handleToggleFavoriteClick(message, e)}
                      secondaryPreview={secondaryPreview}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Popover>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        sx={overlaySx}
        PaperProps={{ sx: { ...surfacePaperSx, minWidth: 160 } }}
      >
        <MenuItem
          onClick={() => {
            const message = messages.find((m) => m.id === selectedMessageId);
            if (message) handleOpenDialog(message);
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            const message = messages.find((m) => m.id === selectedMessageId);
            if (message) void toggleFavoriteFor(message);
            handleMenuClose();
          }}
        >
          {messages.find((m) => m.id === selectedMessageId)?.is_favorite ? (
            <>
              <StarBorderIcon sx={{ mr: 1, fontSize: 18 }} />
              Remover favorito
            </>
          ) : (
            <>
              <StarIcon sx={{ mr: 1, fontSize: 18 }} />
              Favoritar
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedMessageId) handleDeleteMessage(selectedMessageId);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Excluir
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        sx={overlaySx}
        PaperProps={{ sx: { ...surfacePaperSx, m: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'text.primary', pb: 1 }}>
          {editingMessage ? 'Editar Mensagem Rápida' : 'Nova Mensagem Rápida'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            variant="outlined"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            sx={{ mb: 2, mt: 0.5 }}
          />

          {canAttachMedia && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Tipo de envio
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={formContentType}
                onChange={(_e, v) => {
                  if (!v) return;
                  setFormContentType(v);
                  if (v === 'text') resetMediaForm();
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="text">Texto</ToggleButton>
                <ToggleButton value="image">
                  <ImageIcon fontSize="small" sx={{ mr: 0.5 }} /> Img
                </ToggleButton>
                <ToggleButton value="video">
                  <VideocamIcon fontSize="small" sx={{ mr: 0.5 }} /> Vídeo
                </ToggleButton>
                <ToggleButton value="audio">
                  <MicIcon fontSize="small" sx={{ mr: 0.5 }} /> Áudio
                </ToggleButton>
              </ToggleButtonGroup>

              {formContentType !== 'text' && (
                <Box sx={{ mb: 2 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={
                      formContentType === 'image'
                        ? 'image/*'
                        : formContentType === 'video'
                          ? 'video/*'
                          : 'audio/*'
                    }
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleMediaFile(f);
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || saving}
                    fullWidth
                  >
                    {formMediaUrl ? 'Trocar arquivo' : 'Anexar arquivo (R2)'}
                  </Button>
                  {uploading && <LinearProgress sx={{ mt: 1 }} />}
                  {formMediaFileName && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {formMediaFileName}
                    </Typography>
                  )}
                  {formContentType === 'audio' && (
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          checked={formVoiceNote}
                          onChange={(e) => setFormVoiceNote(e.target.checked)}
                        />
                      }
                      label="Enviar como mensagem de voz (gravado na hora)"
                    />
                  )}
                </Box>
              )}
            </>
          )}

          <TextField
            margin="dense"
            label={formContentType === 'text' ? 'Mensagem' : 'Legenda (opcional)'}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={formMessage}
            onChange={(e) => setFormMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={saving || uploading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveMessage}
            variant="contained"
            disabled={!formValid() || saving || uploading}
          >
            {editingMessage ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickMessagesButton;
